import { Component, ElementRef, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatTabGroup } from '@angular/material/tabs';

import { TranslocoService } from '@ngneat/transloco';
import { combineLatest, catchError, finalize, Observable, of, Subject, take, takeUntil, tap, throwError } from 'rxjs';
import { SvcReactionChangeEvent } from '@solvace/feature/general/svc-reaction';

import { SvcModalBos, SvcModalBosCombos } from './shared/interfaces/svc-modal-bos.interface';
import { SvcBosCustomFieldsFeatureName, SvcModalBosType, SvcModalBosUserRole } from './shared/enums/svc-modal-bos.enum';
import { SvcBosCombosService } from './shared/services/svc-bos-combos.service';
import { SvcBosErrorService } from './shared/services/svc-bos-error.service';
import { ISvcChipOption } from '@solvace/component/svc-controls';
import { UserAvatarSize } from '@solvace/component/svc-user-avatar';
import { SvcDialogService } from '@solvace/component/svc-dialogs';
import { SvcToastService } from '@solvace/component/svc-toast';
import { UserEnvironment } from 'projects/lib-shared-core/src/lib/features/user/services/user.types';
import { UserService } from 'projects/lib-shared-core/src/lib/features/user/services/user.service';
import { AutoDestroy } from 'projects/lib-shared-common/src/lib/decorators/auto-destroy';
import { SvcModalBosTabBehaviorsComponent } from './shared/components/svc-modal-bos-tab-behaviors/svc-modal-bos-tab-behaviors.component';
import { SvcBosFeedbackService } from './shared/services/svc-bos-feedback.service';
import { SvcBosAddFeedback, SvcBosAddFeedbackResponse, SvcBosFeedbackRegister } from './shared/interfaces/svc-bos-feedback.interface';
import { SvcModalBosService } from './shared/services/svc-modal-bos.service';
import { SvcAccessPermissionService } from '../../admin/svc-access-permission/svc-access-permission.service';
import { SolvaceRumService, StaticApplicationId, SvcGlobalDataPrefetching, SvcViewsService } from 'projects/lib-shared-core/src/public-api';
import { UserRoleItem } from '../../admin/svc-access-permission/models/user-role.model';
import { AuthService } from 'projects/lib-shared-core/src/lib/auth/auth.service';
import { SvcFormCustomFieldComponent } from '../../general/svc-form-custom-field/svc-form-custom-field.component';
import { SvcActionplanComponent } from '@solvace/feature/general/svc-actionplan/svc-actionplan.component';
import { SvcLocationFieldComponent } from '@solvace/feature/general/svc-location-field';
import { SvcTotalsStatusComponent } from '@solvace/feature/general/svc-totals-status';
import { SvcPrinterService } from '../../general/svc-printer/svc-printer.service';
import { SvcDialogPrimaryComponent } from 'projects/lib-shared-component/src/lib/svc-dialogs/components/svc-dialog-primary/svc-dialog-primary.component';
import { ISvcButtonEllipsisOption } from '@solvace/business-component/buttons/svc-button-ellipsis';

@Component({
  selector: 'svc-modal-bos',
  templateUrl: './svc-modal-bos.component.html',
  styleUrls: ['./svc-modal-bos.component.scss'],
  host: {
    '[class.modal-ready]': 'modalConfig?.mode !== modalTypes.add || typeSelected?.label',
  },
})
export class SvcModalBosComponent implements OnInit {
  @AutoDestroy public destroy$ = new Subject<void>();
  private readonly _rum = inject(SolvaceRumService);

  @ViewChild('locationField') public locationField: SvcLocationFieldComponent;
  @ViewChild('customField') public customField: SvcFormCustomFieldComponent;
  public tabBehaviors: SvcModalBosTabBehaviorsComponent;

  @ViewChild('tabBehaviors') public set setTabBehaviors(component: SvcModalBosTabBehaviorsComponent) {
    this.tabBehaviors = component;
    if (component && this.bos?.behaviors) component.patchBehaviors(this.bos.behaviors);
  }

  @ViewChild('actionPlan') public actionPlan: SvcActionplanComponent;
  @ViewChild('totalsStatus') public totalsStatus: SvcTotalsStatusComponent;
  @ViewChild(SvcDialogPrimaryComponent) public dialog: SvcDialogPrimaryComponent;
  @ViewChild('tabgroup') public tabgroup: MatTabGroup;
  @ViewChild('bosFormContainer') public bosFormContainer: ElementRef<HTMLElement>;
  @ViewChild('behaviorsTab') public behaviorsTab: ElementRef<HTMLElement>;
  @ViewChild('actionPlanTab') public actionPlanTab: ElementRef<HTMLElement>;

  public modalTypes = SvcModalBosType;
  public typeSelected: Partial<ISvcChipOption>;
  public type = new FormControl();
  public showTypeField = true;
  public canShowTypeModal = false;
  public isCheckingTypeSelection = false;
  public environment: UserEnvironment = this._userService.environment;
  public isLoading: boolean = true;
  public isSaveLoading: boolean = false;
  public isAllCombosLoading = true;
  public isViewLoading = true;
  public formBos: FormGroup;
  public openPlans: number;
  public completePlans: number;
  public rolesCreatePermission: string[] = [SvcModalBosUserRole.Administrator, SvcModalBosUserRole.Analyst];
  public optionsCombos: SvcModalBosCombos = {};
  public maxDate: Date = new Date();
  public bos: SvcBosFeedbackRegister;
  public userHasPermissionToEdit: boolean;
  public customFieldsFeature = SvcBosCustomFieldsFeatureName;
  public loadedPermissionUser: boolean;
  public observerEnabled: boolean;
  public isLoadingObservers: boolean;
  public isLoadingTeams: boolean;
  public isShowTabActionPlanLoading = true;
  public isMandatoryTeamFieldLoading = true;
  public showTabActionPlan = false;
  public isTeamFieldActive = false;
  public isTeamFieldMandatory = false;
  public originatorName: string;
  public originatorId: string;
  public originatorAvatarSize = UserAvatarSize.Table;
  public applicationId = StaticApplicationId.bos;
  public legacyApplicationId = StaticApplicationId.actionPlan;
  public featureName = 'view-bos';
  public dialogActionOptions: ISvcButtonEllipsisOption[] = [];
  public isReactionLoading = false;
  public fromAddMode = false;

  protected readonly SvcModalBosType = SvcModalBosType;

  public get shouldDisplayTypeModal(): boolean {
    return this.modalConfig?.mode === this.modalTypes.add && !this.typeSelected?.label && this.canShowTypeModal;
  }

  public get isInitialLoading(): boolean {
    return (
      this.isLoading ||
      this.isAllCombosLoading ||
      this.isShowTabActionPlanLoading ||
      this.isMandatoryTeamFieldLoading ||
      (this.locationField?.isLoading ?? true) ||
      (this.customField?.isLoading ?? true) ||
      (this.tabBehaviors?.isLoading ?? true) ||
      this.actionPlan?.isLoading
    );
  }

  constructor(
    public dialogRef: MatDialogRef<SvcModalBosComponent>,
    @Inject(MAT_DIALOG_DATA) public modalConfig: SvcModalBos,
    private _combosService: SvcBosCombosService,
    private _errorService: SvcBosErrorService,
    private _userService: UserService,
    private _fb: FormBuilder,
    private _translocoService: TranslocoService,
    private _datePipe: DatePipe,
    private _feedbackService: SvcBosFeedbackService,
    private _toastService: SvcToastService,
    private _modalBosService: SvcModalBosService,
    private _modalService: SvcDialogService,
    private _accessPermissionService: SvcAccessPermissionService,
    private _authService: AuthService,
    private _globalDataPrefetching: SvcGlobalDataPrefetching,
    private _svcViewsService: SvcViewsService,
    private _printerService: SvcPrinterService,
  ) {}

  public ngOnInit(): void {
    this.fromAddMode = this.modalConfig?.mode === this.modalTypes.add;
    this.isCheckingTypeSelection = this.modalConfig?.mode === this.modalTypes.add;
    this._initFormBos();
    this._ensureTypeFieldVisibilityForEditAndView();
    if (this.fromAddMode) {
      this.originatorName = this._userService.user?.firstLastName;
      this.originatorId = this._userService.userId$;
      this._rum.track('record.create', { module: 'bos', feature: 'new-record' });
    }
    if (this.modalConfig.mode !== this.modalTypes.add) this.getData();
    this.getShowTabActionPlan();
    this.getMandatoryTeamField();
    this._closePrinter();
  }

  public getData(): void {
    this._getFormAllCombos();
    this._getFeedbackById();
    this._getObserverEnabled();
  }

  private _getObservers(): void {
    if (this.observerEnabled) {
      this.isLoadingObservers = true;
      this._combosService
        .getObserversUsersAndGuests()
        .pipe(
          tap(
            (observers) =>
              (this.optionsCombos = {
                ...this.optionsCombos,
                observers: observers,
              })
          ),
          catchError((err) => this._showError(err)),
          finalize(() => (this.isLoadingObservers = false)),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }
  }

  public onOriginatorChange(event: { option: ISvcChipOption; selected: boolean }): void {
    const originatorId = event?.option?.value as string;
    this._getTeams(originatorId);
  }

  public _getTeams(originatorId: string): void {
    this.isLoadingTeams = true;
    this._combosService
      .getTeams(true, originatorId)
      .pipe(
        tap(
          (observers) =>
            (this.optionsCombos = {
              ...this.optionsCombos,
              teams: observers,
            })
        ),
        catchError((err) => this._showError(err)),
        finalize(() => (this.isLoadingTeams = false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private _getObserverEnabled(): void {
    this._globalDataPrefetching.data.bosShowOriginator.value$
      .pipe(
        takeUntil(this.destroy$),
        tap((value) => {
          this.observerEnabled = value;
          if (this.modalConfig.mode === this.modalTypes.add) this._getTeams(this._userService.userId$);
          this._getObservers();
        })
      )
      .subscribe();
  }

  private _getFeedbackById(): void {
    if (this.modalConfig.mode === SvcModalBosType.add) {
      this.isLoading = false;
      return;
    }

    const uniqueId = (this.modalConfig as Partial<{ uniqueId: number }>)?.uniqueId as number | undefined;
    const hasUniqueId = typeof uniqueId === 'number' && !isNaN(uniqueId);
    const canUseId = typeof this.modalConfig?.id === 'number' && !isNaN(this.modalConfig.id as number);

    if (!hasUniqueId && !canUseId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    const request$ = hasUniqueId ? this._feedbackService.getFeedbackByUniqueId(uniqueId) : this._feedbackService.getFeedbackById(this.modalConfig.id as number);

    request$
      .pipe(
        tap((bos: SvcBosFeedbackRegister) => {
          this.bos = bos;
          this.customField?.setCustomFields(bos?.customFieldValue, bos?.typeId);
          this._patchFormBos();
          this._getPermissionsUser();
          this._registerView();
        }),
        catchError((err) => {
          this.formBos?.disable({ emitEvent: false });
          return this._showError(err);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private _getPermissionsUser(): void {
    if (this.modalConfig.mode === SvcModalBosType.view && !this.loadedPermissionUser) {
      this._accessPermissionService.getRolesByUser(StaticApplicationId.bos).subscribe({
        next: (userRoles: UserRoleItem[]) => {
          this.loadedPermissionUser = true;
          const originatorId = this.formBos?.value?.creatorUserId?.[0]?.toLowerCase();
          const userId = this._authService.userId?.toLowerCase();

          this.userHasPermissionToEdit = userRoles.some((userRole: UserRoleItem) => userRole.roleId === SvcModalBosUserRole.Administrator) || originatorId === userId;
          this._setMoreOptions();
        },
        error: (err) => this._errorService.showErrorInToast(err),
      });
    }
  }

  private _getOptionsByBosCombo(keyName: string, keyValue: string): ISvcChipOption[] {
    return this.bos[keyName] ? [{ label: this.bos[keyName], value: this.bos[keyValue] }] : [];
  }

  private _getPatchValue(keyName: string): number[] {
    return this.bos?.[keyName] ? [this.bos?.[keyName]] : [];
  }
  public onBookmarkChanged(value: boolean): void {
    this._modalBosService.bookmarkStatusUpdated$.next(this.bos?.feedbackId);
    const messageKey = value ? 'adicionado aos' : 'removido dos';
    this._toastService.success(this._translocoService.translate(`Registro ${messageKey} favoritos com sucesso`));
  }

  private _setCombosWithDefaultData(): void {
    if (this.isAllCombosLoading) {
      this.optionsCombos = {
        ...this.optionsCombos,
        teams: this._getOptionsByBosCombo('teamName', 'teamId'),
        contractors: this._getOptionsByBosCombo('contractorName', 'contractorId'),
        shifts: this.bos.shiftName ? [{ label: this._translocoService.translate(this.bos?.shiftName), value: this.bos?.shiftId }] : [],
      };
    }
  }

  private _patchFormBos(): void {
    this.originatorName = this.bos?.userAvatar?.firstLastName;
    this.originatorId = this.bos?.userAvatar?.userId;
    this._getTeams(this.originatorId);

    this._setCombosWithDefaultData();

    this.setTypeSelected({
      label: this.bos.typeName,
      value: this.bos.typeId,
    });

    this.formBos?.patchValue({
      ...this.bos,
      feedbackId: `${this._translocoService.translate('BOS')}-${this.bos?.feedbackId}`,
      locationId: this._getPatchValue('locationId'),
      teamId: this._getPatchValue('teamId'),
      shiftId: this._getPatchValue('shiftId'),
      contractorId: this._getPatchValue('contractorId'),
      openDate: this.bos?.openDate,
      feedbackDate: this.bos?.feedbackDate,
      creatorUserId: this._getPatchValue('creatorUserId'),
    });

    this.formBos?.disable({ emitEvent: false });
    this.formBos?.get('openDate')?.disable();
    this.formBos?.get('feedbackDate')?.disable();
    this.tabBehaviors?.patchBehaviors(this.bos?.behaviors);
  }

  private _initFormBos(): void {
    const isAddMode = this.modalConfig?.mode === this.modalTypes.add;

    this.formBos = this._fb.group({
      feedbackId: [`${this._translocoService.translate('BOS')}-`],
      locationId: [null, Validators.required],
      teamId: [null, []],
      shiftId: [null, Validators.required],
      contractorId: [null],
      openDate: [{ value: this._datePipe.transform(new Date(), 'yyyy-MM-dd'), disabled: true }],
      feedbackDate: [this._datePipe.transform(new Date(), 'yyyy-MM-dd'), Validators.required],
      creatorUserId: [isAddMode ? [this._userService.userId$] : null, Validators.required],
    });
  }

  public setTypeSelected(type: ISvcChipOption): void {
    this.typeSelected = type;
    this.type.setValue(type.label);
  }

  public handleSingleTypeDetected(hasSingleType: boolean): void {
    this.showTypeField = !hasSingleType;
    this.canShowTypeModal = !hasSingleType;
    this.isCheckingTypeSelection = false;
  }

  public onFinishLoadType(): void {
    this.isCheckingTypeSelection = false;
    if (this.showTypeField) {
      this.canShowTypeModal = true;
    }
    this.getData();
  }

  private _ensureTypeFieldVisibilityForEditAndView(): void {
    if (this.modalConfig?.mode === this.modalTypes.add) return;

    this._combosService
      .getTypes(true)
      .pipe(
        tap((types) => {
          const hasSingleType = (types ?? []).length === 1;
          this.showTypeField = !hasSingleType;
        }),
        catchError((err) => this._showError(err)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private _getFormAllCombos(): void {
    this.isAllCombosLoading = true;
    this._combosService
      .getAllCombosToRegisterBos()
      .pipe(
        tap((combos: SvcModalBosCombos) => {
          this.optionsCombos = {
            ...this.optionsCombos,
            ...combos,
          };
        }),
        catchError((err) => this._showError(err)),
        finalize(() => (this.isAllCombosLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private _showError(err: any): Observable<never> {
    this._errorService.showErrorInToast(err);
    return throwError(() => err);
  }

  private _getBodyFeedback(isUpdate: boolean): SvcBosAddFeedback {
    const bodyFeedback: SvcBosAddFeedback = {
      feedbackDate: this.formBos?.value?.feedbackDate,
      typeId: Number(this.typeSelected?.value),
      openDate: this.formBos?.controls?.openDate?.value,
      locationId: this.formBos?.value?.locationId?.[0],
      behaviors: this.tabBehaviors.getFormValue(),
      contractorId: this.formBos?.value?.contractorId?.[0],
      shiftId: this.formBos?.value?.shiftId?.[0],
      teamId: this.formBos?.value?.teamId?.[0],
      creatorUserId: this.formBos?.value?.creatorUserId?.[0],
      ...(this.customField?.hasFields() ? { customFieldValue: this.customField?.getFieldsData() } : {}),
    };

    Object.keys(bodyFeedback)?.forEach((key) => {
      if (!bodyFeedback?.[key]) delete bodyFeedback?.[key];
    });

    if (isUpdate) {
      delete bodyFeedback?.typeId;
      delete bodyFeedback?.openDate;
      bodyFeedback.feedbackId = this.modalConfig.id;
    }

    return bodyFeedback;
  }

  public _getRequestToFeedback(isUpdate: boolean, bodyFeedback: SvcBosAddFeedback): Observable<SvcBosAddFeedbackResponse> {
    return isUpdate ? this._feedbackService.updateFeedback(bodyFeedback) : this._feedbackService.createFeedback(bodyFeedback);
  }

  private _successCreateOrUpdate(isUpdate: boolean, feedbackResponse: SvcBosAddFeedbackResponse): void {
    this.formBos?.controls?.feedbackId?.setValue(`${this._translocoService.translate('BOS')}-${feedbackResponse.feedbackId}`);
    this.modalConfig = {
      mode: SvcModalBosType.view,
      id: feedbackResponse.feedbackId,
    };
    this._getPermissionsUser();
    const message = isUpdate ? 'Alteração salva com sucesso' : 'BOS incluído com sucesso';
    this._toastService.success(this._translocoService.translate(message));
    this.tabBehaviors.setBehaviorsHidden();
    this.formBos.disable({ emitEvent: false });
    this._modalBosService.bosAdd$.next();
    this.originatorId = this.formBos?.value?.creatorUserId?.[0] ?? this.originatorId;
    this._getTeams(this.originatorId);
    if (this.observerEnabled) this.originatorName = (this.optionsCombos?.observers?.find((option) => option.value === this.originatorId)?.label as string) ?? '';
  }

  public createOrUpdateFeedback(isUpdate = false): void {
    if (this.isSaveLoading) return;
    this.formBos?.markAllAsTouched();

    if (this.formBos?.valid && this.customField?.formValid()) {
      this.isSaveLoading = true;

      const bodyFeedback: SvcBosAddFeedback = this._getBodyFeedback(isUpdate);
      const request$ = this._getRequestToFeedback(isUpdate, bodyFeedback);
      request$
        .pipe(
          tap((feedbackResponse: SvcBosAddFeedbackResponse) => {
            if (!isUpdate) {
              this.bos = {} as SvcBosFeedbackRegister;
              this.bos.feedbackId = feedbackResponse.feedbackId;
              this.bos.isBookmarked = false;
              this._rum.track('record.submit', { module: 'bos', feature: 'new-record' });
            }
            this._dispatchEventToLegacy(bodyFeedback, feedbackResponse);
            this._successCreateOrUpdate(isUpdate, feedbackResponse);
          }),
          catchError((err) => {
            this.isSaveLoading = false;
            return this._showError(err);
          }),
          finalize(() => (this.isSaveLoading = false)),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }
  }

  private _dispatchEventToLegacy(bodyFeedback: SvcBosAddFeedback, feedbackResponse: SvcBosAddFeedbackResponse): void {
    window.parent.dispatchEvent(
      new CustomEvent(bodyFeedback?.feedbackId ? 'UpdateBosCallback' : 'CreateBosCallback', {
        detail: {
          typeName: this.typeSelected.label,
          feedbackId: feedbackResponse.feedbackId,
        },
      })
    );
  }

  public setEditMode(): void {
    if (this.modalConfig.mode === SvcModalBosType.view) {
      this.customField.formCustomFields.enable();
      this.tabBehaviors.showAllBehaviors();
      this.modalConfig.mode = SvcModalBosType.edit;
      this.formBos.enable();
      this.formBos.get('openDate').disable();
      this.formBos.get('feedbackDate').enable();
    } else this.createOrUpdateFeedback(true);
  }

  public deleteBos(): void {
    if (!this.userHasPermissionToEdit) return;

    const text = 'Tem certeza que deseja remover este BOS?';
    this._modalService.openConfirm(text).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isSaveLoading = true;
        this._feedbackService
          .deleteFeedback(this.modalConfig.id)
          .pipe(
            tap(() => {
              this._modalBosService.bosAdd$.next();
              const message = this._translocoService.translate('BOS removido com sucesso.');
              this._toastService.success(message);
              this.dialogRef.close();
            }),
            catchError((err) => this._showError(err)),
            finalize(() => (this.isSaveLoading = false)),
            takeUntil(this.destroy$)
          )
          .subscribe();
      }
    });
  }

  private getShowTabActionPlan(): void {
    this.isShowTabActionPlanLoading = true;
    this._globalDataPrefetching.data.bosShowTabActionPlan.value$
      .pipe(
        takeUntil(this.destroy$),
        tap((value) => {
          this.showTabActionPlan = value;
          this.isShowTabActionPlanLoading = false;
        })
      )
      .subscribe();
  }
  private getMandatoryTeamField(): void {
    this.isMandatoryTeamFieldLoading = true;
    combineLatest([
      this._globalDataPrefetching.data.bosTeamFieldIsActive.value$,
      this._globalDataPrefetching.data.bosTeamFieldIsRequired.value$,
    ]).pipe(
      takeUntil(this.destroy$),
      tap(([active, mandatory]) => {
        this.isTeamFieldActive = active;
        this.isTeamFieldMandatory = active && mandatory;
        const teamCtrl = this.formBos.get('teamId');
        if (teamCtrl) {
          if (this.isTeamFieldActive && this.isTeamFieldMandatory) {
            teamCtrl.setValidators([Validators.required]);
          } else {
            teamCtrl.clearValidators();
            if (!this.isTeamFieldActive) teamCtrl.setValue(null);
          }
          if (teamCtrl.touched) teamCtrl.updateValueAndValidity();
        }
        this.isMandatoryTeamFieldLoading = false;
      })
    ).subscribe();
  }

  private _registerView(): void {
    if (this.modalConfig.mode == this.modalTypes.add) return;
    this.isViewLoading = true;
    this._svcViewsService
      .setViews({
        registryId: this.bos.feedbackId?.toString(),
        applicationId: StaticApplicationId.bos,
        featureName: this.featureName,
      })
      .pipe(
        finalize(() => {
          this.totalsStatus?.refresh({ reactions: true, views: true });
          this._modalBosService.bosUpdated$.next(this.bos?.feedbackId);
          this.isViewLoading = false;
        })
      )
      .subscribe();
  }

  public onReactionChange(event: SvcReactionChangeEvent): void {
    if (event && !this.isReactionLoading) {
      (event.anticipated?.onReactionDone$ ?? of({ success: true }))
        .pipe(
          take(1),
          tap((res: any) => {
            if (res?.success) {
              this.totalsStatus?.refresh({ reactions: true, views: true });
              this._modalBosService.bosUpdated$.next(this.bos?.feedbackId);
            }
          })
        )
        .subscribe();
    }
  }

  private _setMoreOptions(): void {
    this.dialogActionOptions = [
      {
        name: this._translocoService.translate('Excluir'),
        icon: 'heroicons_solid:trash',
        action: (): void => this.deleteBos(),
        hidden: !this.userHasPermissionToEdit,
      },
      {
        name: this._translocoService.translate('Imprimir'),
        icon: 'heroicons_solid:printer',
        action: (): void => this.print(),
      },
    ];
  }

  public print(): void {
    this.tabBehaviors.openAllPanels();
    const dataToPrint = {
      options: [
        {
          name: 'Header',
          elements: [this.dialog.dialogTitleContainer?.nativeElement],
          show: false,
        },
        {
          name: 'BOSBasicInfo',
          elements: [this.bosFormContainer?.nativeElement],
          show: false,
        },
        {
          name: this._translocoService.translate('BOS'),
          elements: [this.behaviorsTab?.nativeElement],
          selected: true,
        },
        ...(this.showTabActionPlan ? [{
          name: this._translocoService.translate('Planos de ação'),
          elements: [this.actionPlanTab?.nativeElement],
        }] : []),
      ],
    };
    this._printerService.open(dataToPrint);
  }

  private _closePrinter(): void {
    this._printerService.closePrinter$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.tabBehaviors.resetOpenPanelsByOriginal(),
    });
  }
}
